import { Injectable, inject, isDevMode, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { GoogleTagManagerService } from 'angular-google-tag-manager';

import { Tag } from '../models';

/**
 * Pushes analytics events onto the GTM dataLayer.
 *
 * This service is deliberately NOT responsible for loading GTM. The container
 * is injected exactly once, at application bootstrap, by the app initializer in
 * `provide-analytics.ts`. Events therefore assume GTM is already present and
 * push straight onto the dataLayer via the library's public `getDataLayer()`.
 *
 * Do NOT reintroduce `pushTag()`/`addGtmToDom()` here. `pushTag()` lazily
 * injects the container on first use — exactly the regression that previously
 * left GTM unloaded on any page that never fired an analytics event (homepage,
 * event listings, blog, paid-social landing pages), causing GA4 visibility
 * loss. Loading responsibility lives in ONE place (the bootstrap initializer)
 * so that visibility can never again depend on event side effects.
 */
@Injectable()
export class AnalyticsService {
  #isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  #gtm = inject(GoogleTagManagerService);
  #warnedMissingGtm = false;

  push(item: Tag): void {
    // GTM and the dataLayer are browser-only; never run during SSR.
    if (!this.#isBrowser) return;

    this.#assertGtmBootstrapped();

    // Push straight onto the dataLayer. This intentionally does NOT load GTM —
    // loading is owned solely by the bootstrap initializer (see class docs).
    // getDataLayer() simply ensures `window.dataLayer` exists and returns it.
    this.#gtm.getDataLayer().push(item);
  }

  /**
   * Dev-only regression tripwire. When GTM is injected correctly at bootstrap,
   * its `#GTMscript` tag is in the DOM before any component can fire an event.
   * If it is missing here, the bootstrap initializer has regressed and GTM has
   * fallen back to (or needs) lazy-loading — warn loudly, once. No-op in prod.
   */
  #assertGtmBootstrapped(): void {
    if (!isDevMode() || this.#warnedMissingGtm) return;
    if (!document.getElementById('GTMscript')) {
      this.#warnedMissingGtm = true;
      console.warn(
        '[AnalyticsService] An analytics event fired but the GTM container is ' +
          'not in the DOM. GTM must be loaded once at bootstrap (see ' +
          'provide-analytics.ts). Analytics events must not be relied upon to ' +
          'load GTM.',
      );
    }
  }
}
